import { useEffect, useState } from "react";

/**
 * PWA install CTA — a small button (or instruction line) that helps
 * a first-time visitor pin Kanji Build to their phone home screen.
 *
 * Three states:
 *   • Android (or Chrome/Edge desktop) — the browser fires
 *     `beforeinstallprompt`. We capture it, show a button that
 *     invokes `deferredPrompt.prompt()` directly. One-tap install.
 *   • iOS Safari — there is no programmatic install API on iOS.
 *     We show a static "open in Safari → Share → Add to Home Screen"
 *     line. Best Apple lets us do.
 *   • Already installed (running standalone) — render nothing. The
 *     user is already inside the installed app and doesn't need to
 *     be nagged.
 */
export default function PWAInstallButton() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already running as a PWA? Bail.
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setInstalled(true);
      return undefined;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Android / Chrome / Edge — we have a deferred prompt, one-tap install.
  if (deferred) {
    return (
      <button
        type="button"
        data-testid="welcome-pwa-install"
        onClick={async () => {
          deferred.prompt();
          await deferred.userChoice;
          // The event is single-use; either way, we clear it.
          setDeferred(null);
        }}
        className="inline-flex items-center gap-2 px-5 py-3 font-sans text-xs uppercase tracking-[0.25em] text-sumi border border-shu/70 hover:bg-shu hover:text-washi transition-colors"
      >
        Install on phone
      </button>
    );
  }

  // iOS Safari — programmatic install isn't possible. Show the recipe.
  if (isIOS) {
    return (
      <p
        data-testid="welcome-pwa-ios-help"
        className="font-sans text-[11px] uppercase tracking-[0.25em] text-sumiSoft max-w-xs leading-relaxed"
      >
        on iphone — tap{" "}
        <span className="text-shu">share → add to home screen</span> in safari
      </p>
    );
  }

  // Everything else (desktop without prompt yet, Firefox, etc.) — a
  // single-line hint. Cheaper than a button that does nothing.
  return (
    <p
      data-testid="welcome-pwa-hint"
      className="font-sans text-[11px] uppercase tracking-[0.25em] text-sumiSoft max-w-xs leading-relaxed"
    >
      install on phone — open kanjibuild.com in{" "}
      <span className="text-shu">chrome (android)</span> or{" "}
      <span className="text-shu">safari (ios)</span>
    </p>
  );
}
